'use client';

/**
 * EditProjectModal Component
 *
 * Modal for editing an existing project's details.
 * Supports updating name, slug, and description with auto-slug generation.
 */

import {
  Alert,
  AlertDescription,
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Stack,
  Text,
  TextArea,
} from '@cortex/ui';
import * as React from 'react';
import { z } from 'zod';

import { useFetchWithAuth } from '@/hooks/use-api';

interface EditProjectModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Project to edit */
  project: {
    id: string;
    name: string;
    slug: string;
    description?: string | null;
  };
  /** Callback when project is successfully updated */
  onSuccess?: () => void;
}

const projectSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      'Slug must be lowercase alphanumeric with hyphens only'
    ),
  description: z.string().optional(),
});

type ProjectFormData = z.infer<typeof projectSchema>;

function generateSlug(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function EditProjectModal({
  open,
  onClose,
  project,
  onSuccess,
}: EditProjectModalProps) {
  const fetchWithAuth = useFetchWithAuth();
  const [name, setName] = React.useState(project.name);
  const [slug, setSlug] = React.useState(project.slug);
  const [description, setDescription] = React.useState(
    project.description ?? ''
  );
  const [slugManuallyEdited, setSlugManuallyEdited] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [validationErrors, setValidationErrors] = React.useState<
    Partial<Record<keyof ProjectFormData, string>>
  >({});

  React.useEffect(() => {
    if (open) {
      setName(project.name);
      setSlug(project.slug);
      setDescription(project.description ?? '');
      setSlugManuallyEdited(false);
      setSaving(false);
      setError(null);
      setValidationErrors({});
    }
  }, [open, project]);

  React.useEffect(() => {
    if (!slugManuallyEdited && name) {
      setSlug(generateSlug(name));
    }
  }, [name, slugManuallyEdited]);

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSlug(e.target.value);
    setSlugManuallyEdited(true);
  };

  const handleSubmit = async () => {
    setError(null);
    setValidationErrors({});

    const result = projectSchema.safeParse({ name, slug, description });
    if (!result.success) {
      const errors: Partial<Record<keyof ProjectFormData, string>> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          errors[err.path[0] as keyof ProjectFormData] = err.message;
        }
      });
      setValidationErrors(errors);
      return;
    }

    setSaving(true);

    try {
      const response = await fetchWithAuth(`/admin/projects/${project.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: result.data.name,
          slug: result.data.slug,
          description: result.data.description || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update project');
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Project</DialogTitle>
          <DialogDescription>Update project details</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <Stack gap="md">
            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Stack gap="xs">
              <Label htmlFor="projectName">Name</Label>
              <Input
                id="projectName"
                type="text"
                placeholder="My Project"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={validationErrors.name ? 'border-destructive' : ''}
              />
              {validationErrors.name && (
                <Text color="error" size="xs" className="mt-1">
                  {validationErrors.name}
                </Text>
              )}
            </Stack>

            <Stack gap="xs">
              <Label htmlFor="projectSlug">Slug</Label>
              <Input
                id="projectSlug"
                type="text"
                placeholder="my-project"
                value={slug}
                onChange={handleSlugChange}
                className={validationErrors.slug ? 'border-destructive' : ''}
              />
              <Text color="muted" size="xs" className="mt-1">
                Lowercase alphanumeric characters and hyphens only
              </Text>
              {validationErrors.slug && (
                <Text color="error" size="xs" className="mt-1">
                  {validationErrors.slug}
                </Text>
              )}
            </Stack>

            <Stack gap="xs">
              <Label htmlFor="projectDescription">Description (Optional)</Label>
              <TextArea
                id="projectDescription"
                placeholder="A description of this project"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
              <Text color="muted" size="xs" className="mt-1">
                Additional context about this project
              </Text>
            </Stack>
          </Stack>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving} loading={saving}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
